import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCompanyScopedStorageKey } from '../../utils/companyScope';

const ServiceContext = createContext();

const readStoredServices = (storageKey) => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.warn('Failed to restore selected services:', error);
    return [];
  }
};

export const useService = () => useContext(ServiceContext);

export const ServiceProvider = ({ children }) => {
  const location = useLocation();
  const storageKey = useMemo(
    () => getCompanyScopedStorageKey('selected-services', location.pathname),
    [location.pathname]
  );

  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    setSelectedServices(readStoredServices(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (selectedServices.length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(selectedServices));
  }, [selectedServices, storageKey]);

  const addService = (service) => {
    setSelectedServices([service]);
  };

  const removeService = (id) => {
    setSelectedServices(prev =>
      prev?.[0]?.id === id ? [] : prev
    );
  };

  const clearServices = () => {
    setSelectedServices([]);

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey);
    }
  };

  return (
    <ServiceContext.Provider
      value={{
        selectedServices,
        addService,
        removeService,
        clearServices
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};
